import { Checkbox } from "primereact/checkbox";
import React, { useState } from "react";
import * as Icon from "react-bootstrap-icons";
import type { ListItem } from "../Controls/AutocompleteInput";

type FilterCategory =
  | "distance"
  | "propertyTypes"
  | "roomTypes"
  | "cities"
  | "locations"
  | "categories";

interface PropertyFilterProps {
  cities: ListItem[];
  locations: ListItem[];
  propertyTypes: ListItem[];
  roomTypes: ListItem[];
  categories: ListItem[];
  selectedCity: ListItem | null;
  selectedLocations: ListItem[];
  selectedPropertyTypes: ListItem[];
  selectedRoomTypes: ListItem[];
  selectedCategories: ListItem[];
  selectedDistance: number | null;
  onCityChange: (city: ListItem | null) => void;
  onLocationsChange: (locations: ListItem[]) => void;
  onPropertyTypesChange: (propertyTypes: ListItem[]) => void;
  onRoomTypesChange: (roomTypes: ListItem[]) => void;
  onCategoriesChange: (categories: ListItem[]) => void;
  onDistanceChange: (distance: number | null) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
}

const PropertyFilter: React.FC<PropertyFilterProps> = ({
  cities,
  locations,
  propertyTypes,
  roomTypes,
  categories,
  selectedCity,
  selectedLocations,
  selectedPropertyTypes,
  selectedRoomTypes,
  selectedCategories,
  selectedDistance,
  onCityChange,
  onLocationsChange,
  onPropertyTypesChange,
  onRoomTypesChange,
  onCategoriesChange,
  onDistanceChange,
  onApplyFilters,
  onClearFilters,
}) => {
  const [showMore, setShowMore] = useState<Record<FilterCategory, boolean>>({
    distance: false,
    propertyTypes: false,
    roomTypes: false,
    cities: false,
    locations: false,
    categories: false,
  });

  const [visible, setVisible] = useState(true);

  const distanceOptions = [
    { label: "Any distance", value: null },
    { label: "Within 5 Km", value: 5 },
    { label: "Within 10 Km", value: 10 },
    { label: "Within 20 Km", value: 20 },
    { label: "Within 50 Km", value: 50 },
  ];

  const toggleShowMore = (category: FilterCategory) => {
    setShowMore((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleDistanceChange = (value: number | null) => {
    onDistanceChange(value);
  };

  // Multi-select toggle handler
  const handleMultiSelectToggle = (
    item: ListItem,
    currentSelection: ListItem[],
    onChange: (items: ListItem[]) => void
  ) => {
    const isSelected = currentSelection.some(
      (selected) => selected.id === item.id
    );
    if (isSelected) {
      onChange(currentSelection.filter((selected) => selected.id !== item.id));
    } else {
      onChange([...currentSelection, item]);
    }
  };

  // Single-select toggle handler (for cities, if needed)
  const handleItemToggle = (
    item: ListItem,
    currentSelection: ListItem | null,
    onChange: (item: ListItem | null) => void
  ) => {
    if (currentSelection && currentSelection.id === item.id) {
      onChange(null);
    } else {
      onChange(item);
    }
  };

  if (!visible) return null;

  return (
    <div
      className="filter-container mt-1"
      style={{ fontSize: "0.85rem", overflowY: "visible", height: "auto" }}
    >
      <div className="filter-header d-flex justify-content-between align-items-center mb-2">
        <h6
          className="filter-title mb-0"
          style={{ fontSize: "0.9rem", fontWeight: "600" }}
        >
          Filter by
        </h6>
        <div className="d-flex justify-content-end gap-1 align-items-center">
          <button
            onClick={onClearFilters}
            className="clear-all btn btn-link p-0 text-decoration-none"
            type="button"
            style={{ fontSize: "0.75rem" }}
          >
            Clear All
          </button>
          <button
            onClick={() => setVisible(false)}
            className="close-all btn btn-light d-inline-block d-none d-md-block"
            type="button"
            aria-label="Close filters"
            style={{ fontSize: "0.75rem", padding: "2px 4px" }}
          >
            <Icon.X />
          </button>
        </div>
      </div>

      <div className="filter-section mb-2">
        <h6
          className="filter-subtitle mb-1"
          style={{ fontSize: "0.8rem", fontWeight: "600" }}
        >
          Distance
        </h6>
        <div className="d-flex flex-column" style={{ gap: "0.25rem" }}>
          {distanceOptions.map((option, idx) => (
            <div
              className="d-flex align-items-center mb-1"
              style={{ gap: "0.35rem" }}
              key={idx}
            >
              <Checkbox
                inputId={`distance-${idx}`}
                value={option.value}
                checked={selectedDistance === option.value}
                onChange={() => handleDistanceChange(option.value)}
              />
              <label
                htmlFor={`distance-${idx}`}
                className="ml-2 mb-0"
                style={{ fontSize: "0.8rem" }}
              >
                {option.label}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Categories Filter - Hidden for now */}
      {/* {categories.length > 0 && (
        <div className="filter-section mt-2">
          <h6
            className="filter-subtitle"
            style={{ fontSize: "0.8rem", fontWeight: "600" }}
          >
            Categories
          </h6>
          <div className="d-flex flex-column" style={{ gap: "0.25rem" }}>
            {categories
              .slice(0, showMore.categories ? categories.length : 5)
              .map((option, idx) => (
                <div
                  className="d-flex align-items-center mb-1"
                  style={{ gap: "0.35rem" }}
                  key={option.id}
                >
                  <Checkbox
                    inputId={`categories-${idx}`}
                    value={option.id}
                    checked={selectedCategories.some(
                      (cat) => cat.id === option.id
                    )}
                    onChange={() =>
                      handleMultiSelectToggle(
                        option,
                        selectedCategories,
                        onCategoriesChange
                      )
                    }
                  />
                  <label
                    htmlFor={`categories-${idx}`}
                    className="ml-2 mb-0"
                    style={{ fontSize: "0.8rem" }}
                  >
                    {option.name}
                  </label>
                </div>
              ))}
          </div>
          {categories.length > 5 && (
            <button
              onClick={() => toggleShowMore("categories")}
              className="btn btn-link p-0 text-decoration-none mt-1"
              type="button"
              style={{ fontSize: "0.75rem" }}
            >
              {showMore.categories ? "Show less" : "See more"}
            </button>
          )}
        </div>
      )} */}

      {propertyTypes.length > 0 && (
        <div className="filter-section mt-2">
          <h6
            className="filter-subtitle"
            style={{ fontSize: "0.8rem", fontWeight: "600" }}
          >
            Property types
          </h6>
          <div className="d-flex flex-column" style={{ gap: "0.25rem" }}>
            {propertyTypes
              .slice(0, showMore.propertyTypes ? propertyTypes.length : 5)
              .map((option, idx) => (
                <div
                  className="d-flex align-items-center mb-1"
                  style={{ gap: "0.35rem" }}
                  key={option.id}
                >
                  <Checkbox
                    inputId={`propertyTypes-${idx}`}
                    value={option.id}
                    checked={selectedPropertyTypes.some(
                      (pt) => pt.id === option.id
                    )}
                    onChange={() =>
                      handleMultiSelectToggle(
                        option,
                        selectedPropertyTypes,
                        onPropertyTypesChange
                      )
                    }
                  />
                  <label
                    htmlFor={`propertyTypes-${idx}`}
                    className="ml-2 mb-0"
                    style={{ fontSize: "0.8rem" }}
                  >
                    {option.name}
                  </label>
                </div>
              ))}
          </div>
          {propertyTypes.length > 5 && (
            <button
              onClick={() => toggleShowMore("propertyTypes")}
              className="btn btn-link p-0 text-decoration-none mt-1"
              type="button"
              style={{ fontSize: "0.75rem" }}
            >
              {showMore.propertyTypes ? "Show less" : "See more"}
            </button>
          )}
        </div>
      )}

      {roomTypes.length > 0 && (
        <div className="filter-section mt-2">
          <h6
            className="filter-subtitle"
            style={{ fontSize: "0.8rem", fontWeight: "600" }}
          >
            Room types
          </h6>
          <div className="d-flex flex-column" style={{ gap: "0.25rem" }}>
            {roomTypes
              .slice(0, showMore.roomTypes ? roomTypes.length : 5)
              .map((option, idx) => (
                <div
                  className="d-flex align-items-center mb-1"
                  style={{ gap: "0.35rem" }}
                  key={option.id}
                >
                  <Checkbox
                    inputId={`roomTypes-${idx}`}
                    value={option.id}
                    checked={selectedRoomTypes.some(
                      (rt) => rt.id === option.id
                    )}
                    onChange={() =>
                      handleMultiSelectToggle(
                        option,
                        selectedRoomTypes,
                        onRoomTypesChange
                      )
                    }
                  />
                  <label
                    htmlFor={`roomTypes-${idx}`}
                    className="ml-2 mb-0"
                    style={{ fontSize: "0.8rem" }}
                  >
                    {option.name}
                  </label>
                </div>
              ))}
          </div>
          {roomTypes.length > 5 && (
            <button
              onClick={() => toggleShowMore("roomTypes")}
              className="btn btn-link p-0 text-decoration-none mt-1"
              type="button"
              style={{ fontSize: "0.75rem" }}
            >
              {showMore.roomTypes ? "Show less" : "See more"}
            </button>
          )}
        </div>
      )}

      {locations.length > 0 && (
        <div className="filter-section mt-2">
          <h6
            className="filter-subtitle"
            style={{ fontSize: "0.8rem", fontWeight: "600" }}
          >
            Locations
          </h6>
          <div className="d-flex flex-column" style={{ gap: "0.25rem" }}>
            {locations
              .slice(0, showMore.locations ? locations.length : 5)
              .map((option, idx) => (
                <div
                  className="d-flex align-items-center mb-1"
                  style={{ gap: "0.35rem" }}
                  key={option.id}
                >
                  <Checkbox
                    inputId={`locations-${idx}`}
                    value={option.id}
                    checked={selectedLocations.some(
                      (loc) => loc.id === option.id
                    )}
                    onChange={() =>
                      handleMultiSelectToggle(
                        option,
                        selectedLocations,
                        onLocationsChange
                      )
                    }
                  />
                  <label
                    htmlFor={`locations-${idx}`}
                    className="ml-2 mb-0"
                    style={{ fontSize: "0.8rem" }}
                  >
                    {option.name}
                  </label>
                </div>
              ))}
          </div>
          {locations.length > 5 && (
            <button
              onClick={() => toggleShowMore("locations")}
              className="btn btn-link p-0 text-decoration-none mt-1"
              type="button"
              style={{ fontSize: "0.75rem" }}
            >
              {showMore.locations ? "Show less" : "See more"}
            </button>
          )}
        </div>
      )}

      <div className="filter-section mt-2">
        <button
          onClick={onApplyFilters}
          className="btn btn-primary w-100"
          type="button"
          style={{ fontSize: "0.8rem", padding: "6px 12px" }}
        >
          Apply Filters
        </button>
      </div>
    </div>
  );
};

export default PropertyFilter;
